import React, { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import memoize from 'lodash/memoize';
import capitalize from 'lodash/capitalize';
import Button from '@atlaskit/button';
import { CloudProductDetailView, NextBillExpiryDate } from '@css-xp/base/entitlement-utils';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/cssxp-client-utils';
import { fetchBillingProfileNameById } from '@css-xp/ppc-common/src/queries/fetch-billing-profile-name-by-id';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { ColGroup, CustomDynamicResultTable, HeadCell, SearchResultColumn, TableBody, TableHead } from '@css-xp/base';
import {
  NoData,
  getSelectedColumns,
  emptyStateProp,
  errorStateProp,
  ORG_DETAILS_COLUMNS,
  getOrgDetailsHeaders,
  renderBillingProfile,
  renderPlan,
  Usage,
} from '@css-xp/ppc-common';
import {
  CloudSiteProduct,
  CloudSubscription,
  InvoiceGroup,
  TransactionAccountProduct,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getStatusForProductSubscription } from '@css-xp/ppc-subscription-table';
import { routePaths } from '@css-xp/ppc-constants';
import { getRouteUrl, isMultiInstanceOffering } from '@css-xp/ppc-utils';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import './products-table-grouped-under-site.scss';

interface Props {
  txaId: string;
  productsAppsAndSubscriptions: any;
  groupedProductDataError: any;
  groupedProductDataLoading: boolean;
  isEmpty: boolean;
  invoiceGroupDetails: InvoiceGroup[];
  invoiceGroupLoading: boolean;
  subscriptionColumnVersion?: number;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  version?: number;
  isMultiInstanceAppsEnabled?: boolean;
}

type ColumnList = HeadCell & SearchResultColumn;

enum COLUMNS {
  PRODUCT = 'Product',
  STATUS = 'Status',
  OFFERING = 'Plan',
  USERS = 'Users',
  BILLING_PROFILE = 'Billing profile',
  BILLING_CYCLE = 'Billing cycle',
  NEXT_BILL_DATE = 'Next bill date',
}

const CellLoader = () => (
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="60%" marginBottom="0" />
  </SkeletonLoaderWrapper>
);

const getHeaders = memoize(() => {
  return [
    {
      name: COLUMNS.PRODUCT,
      content: COLUMNS.PRODUCT,
      customClassNames: 'col-20',
    },
    {
      name: COLUMNS.STATUS,
      content: COLUMNS.STATUS,
    },
    {
      name: COLUMNS.OFFERING,
      content: COLUMNS.OFFERING,
    },
    {
      name: COLUMNS.USERS,
      content: COLUMNS.USERS,
    },
    {
      name: COLUMNS.BILLING_PROFILE,
      content: COLUMNS.BILLING_PROFILE,
    },
    {
      name: COLUMNS.BILLING_CYCLE,
      content: COLUMNS.BILLING_CYCLE,
    },
    {
      name: COLUMNS.NEXT_BILL_DATE,
      content: COLUMNS.NEXT_BILL_DATE,
    },
  ];
});

const ansClient = abgGraphqlClient(ProxyGraphClients.ANS);

export const getBillingProfileName = async (id: string, txaId: string) => {
  const { data } = await ansClient.query({
    query: fetchBillingProfileNameById,
    variables: {
      id: id ?? '',
      txaId,
    },
  });

  return data;
};

const ProductsTableGroupedUnderSite = ({
  txaId,
  productsAppsAndSubscriptions,
  groupedProductDataError,
  groupedProductDataLoading,
  isEmpty,
  invoiceGroupDetails,
  invoiceGroupLoading,
  subscriptionColumnVersion,
  isSandboxSupportForSubscriptionsEnabled = false,
  version,
  isMultiInstanceAppsEnabled = false,
}: Props) => {
  const [isProductRowExpanded, setIsProductRowExpanded] = useState<boolean>(false);
  const [productRowExpanded, setProductRowExpanded] = useState<Record<string, boolean>>({});
  const [columnsList] = useState<Array<ColumnList>>(version === 3 ? getOrgDetailsHeaders() : getHeaders());
  const subscriptionsRoutePPC = txaId && getRouteUrl(routePaths.subscriptions, { txaId: txaId });
  const [billingProfileNameMap, setBillingProfileNameMap] = useState<Record<string, any>>({});

  useEffect(() => {
    if (version === 3) {
      const invoiceGroupIds: string[] = [];
      Object.keys(productsAppsAndSubscriptions).forEach((entitlementName: string) => {
        const productAppAndSubscription = productsAppsAndSubscriptions[entitlementName];
        const products = productAppAndSubscription?.products ?? [];
        const apps = productAppAndSubscription?.apps ?? [];
        [...products, ...apps].forEach((record) => invoiceGroupIds.push(record.invoiceGroupId));
      });

      if (invoiceGroupIds.length) {
        Promise.allSettled(
          invoiceGroupIds.map(async (invoiceGroupId) => {
            if (invoiceGroupId) {
              const data = await getBillingProfileName(invoiceGroupId, txaId);
              return { [invoiceGroupId]: data };
            }
          })
        ).then((data) => {
          if (data) {
            setBillingProfileNameMap(
              data.reduce((init, billingProfile: any) => {
                return { ...init, ...billingProfile?.value };
              }, {})
            );
          }
        });
      }
    }
  }, []);

  const checkIsProductRowExpanded = (entitlementName?: string) =>
    //Using version 3 here as the related changes were already implemented with version 3 check.
    version === 3 ? productRowExpanded[entitlementName ?? ''] : isProductRowExpanded;

  const renderSubscription = (
    productSubscription: CloudSiteProduct,
    showExpandButton?: boolean,
    entitlementName?: string
  ) => {
    return (
      <div className="product-expand-container">
        {showExpandButton && (
          <Button
            iconBefore={
              checkIsProductRowExpanded(entitlementName) ? (
                <ChevronDownIcon label="collapse" />
              ) : (
                <ChevronRightIcon label="expand" />
              )
            }
            onClick={() => {
              if (version === 3) {
                if (entitlementName) {
                  setProductRowExpanded({
                    ...productRowExpanded,
                    [entitlementName]: !productRowExpanded[entitlementName],
                  });
                }
              } else {
                setIsProductRowExpanded(!isProductRowExpanded);
              }
            }}
            appearance="subtle"
            testId="apps-expand-collapse-button"
          />
        )}
        <CloudProductDetailView
          productId={productSubscription?.productId as string}
          productName={productSubscription?.productName}
          productLink={`${subscriptionsRoutePPC}${productSubscription?.id}`}
          logoId={productSubscription?.logoId as string}
          version={subscriptionColumnVersion}
          entitlementNumber={productSubscription?.entitlementNumber ?? ''}
          isSandbox={(isSandboxSupportForSubscriptionsEnabled && productSubscription?.isSandbox) ?? false}
          isAddOn={!!productSubscription?.isAddOn}
          isExternallyBilled={productSubscription?.isExternallyBilled}
        />
      </div>
    );
  };

  const getRowData = (productSubscription: CloudSiteProduct, showExpandButton?: boolean, entitlementName?: string) => {
    const columns: (HeadCell & SearchResultColumn)[] = getSelectedColumns(columnsList);
    const isAppInstanceOfMultiInstance =
      isMultiInstanceAppsEnabled &&
      !!productSubscription?.productId &&
      isMultiInstanceOffering(productSubscription?.offering, productSubscription.productId);

    if (version === 3) {
      const rowData = columns.map((column) => {
        switch (column.name) {
          case ORG_DETAILS_COLUMNS.SUBSCRIPTION:
            return <td>{renderSubscription(productSubscription, showExpandButton, entitlementName)}</td>;
          case ORG_DETAILS_COLUMNS.STATUS:
            return getStatusForProductSubscription(productSubscription as TransactionAccountProduct);
          case ORG_DETAILS_COLUMNS.PLAN:
            return <td>{renderPlan(productSubscription)}</td>;
          case ORG_DETAILS_COLUMNS.QUANTITY:
            return (
              <td className="col-usage">
                <Usage
                  activeUsers={productSubscription?.activeUsers}
                  chargeElement={productSubscription?.chargeElement}
                  edition={productSubscription?.edition}
                  productId={productSubscription?.productId}
                  userTier={productSubscription?.userTier}
                />
              </td>
            );
          case ORG_DETAILS_COLUMNS.BILLING_PROFILE:
            return (
              <td>
                {renderBillingProfile(
                  productSubscription,
                  txaId,
                  billingProfileNameMap[productSubscription.invoiceGroupId || '']
                )}
              </td>
            );
          case ORG_DETAILS_COLUMNS.RENEW_DATE:
            return (
              <td>
                {isAppInstanceOfMultiInstance ? (
                  <NoData testId="no-data-end-date" />
                ) : (
                  <NextBillExpiryDate item={productSubscription as CloudSubscription} />
                )}
              </td>
            );
        }
      });
      return rowData;
    }

    const rowData = columns.map((column) => {
      switch (column.name) {
        case COLUMNS.PRODUCT:
          return <td>{renderSubscription(productSubscription, showExpandButton)}</td>;
        case COLUMNS.STATUS:
          return getStatusForProductSubscription(productSubscription as TransactionAccountProduct);
        case COLUMNS.OFFERING:
          return <td>{productSubscription?.offering?.name ?? productSubscription?.edition ?? <NoData />}</td>;
        case COLUMNS.USERS:
          return <td> {productSubscription?.activeUsers} </td>;
        case COLUMNS.BILLING_PROFILE:
          return (
            <td>
              {invoiceGroupLoading ? (
                <CellLoader />
              ) : (
                <Link
                  to={`${getRouteUrl(routePaths.billingProfileDetails, {
                    txaId: txaId,
                    billingProfileId: `${encodeURIComponent(productSubscription?.invoiceGroupId ?? '')}`,
                  })}`}
                >
                  {
                    invoiceGroupDetails?.find(
                      (invoiceGroup) => invoiceGroup?.id === productSubscription?.invoiceGroupId
                    )?.name
                  }
                </Link>
              )}
            </td>
          );
        case COLUMNS.BILLING_CYCLE:
          return (
            <td>
              {productSubscription?.billingFrequency ? capitalize(productSubscription?.billingFrequency) : <NoData />}
            </td>
          );
        case COLUMNS.NEXT_BILL_DATE:
          return (
            <td>
              {isAppInstanceOfMultiInstance ? (
                <NoData testId="no-data-end-date" />
              ) : (
                <NextBillExpiryDate item={productSubscription as CloudSubscription} />
              )}
            </td>
          );
      }
    });
    return rowData;
  };

  const renderTable = () => {
    const children = (
      <>
        <ColGroup columns={getSelectedColumns(columnsList)} />
        <TableHead columns={getSelectedColumns(columnsList)} />
        <TableBody>
          {Object.keys(productsAppsAndSubscriptions).map((entitlementName: string, index: number) => {
            //@ts-ignore
            const productAppAndSubscription = productsAppsAndSubscriptions[entitlementName];
            const products = productAppAndSubscription?.products ?? [];
            const apps = productAppAndSubscription?.apps ?? [];

            return (
              <React.Fragment key={index}>
                {products.map((productSubscription: CloudSiteProduct, productIndex: number) => {
                  const isLastProductAndHasApps = apps.length > 0 && productIndex === products.length - 1;
                  return (
                    <tr
                      key={productSubscription?.id}
                      className={classNames({
                        'product-app-line-item': isLastProductAndHasApps,
                      })}
                    >
                      {getRowData(productSubscription, isLastProductAndHasApps, entitlementName)}
                    </tr>
                  );
                })}
                {checkIsProductRowExpanded(entitlementName) &&
                  apps.map((appSubscription: CloudSiteProduct) => {
                    return (
                      <tr key={appSubscription?.id} className="app-line-item">
                        {getRowData(appSubscription)}
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </>
    );
    return (
      <CustomDynamicResultTable
        className={classNames('products-under-site-container-table')}
        noResults={isEmpty}
        error={groupedProductDataError?.toString()}
        isLoading={groupedProductDataLoading}
        emptyStateProps={emptyStateProp}
        errorStateProps={errorStateProp}
        isPaginationEnabled={false}
      >
        {children}
      </CustomDynamicResultTable>
    );
  };

  return (
    <div
      className={classNames('products-under-site-container', {
        'products-under-site-container-v3': version === 3,
      })}
    >
      {renderTable()}
    </div>
  );
};

export default memo(ProductsTableGroupedUnderSite);
